import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CommentData, CommentsQueryParams, NewsArticleComment } from '@models/index';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { formatToISODate, formatToISODateEndOfDay, isNotNullOrUndefined } from 'cui-components';
import { catchError, EMPTY, filter, pipe, switchMap, take, tap } from 'rxjs';
import { CommentsFiltersComponent } from 'src/app/app-pages/marketing/comments/comments-filters.component';
import {
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  getAllWithParams,
  getOneWithParams,
  StateDictionary,
} from '../../helpers';
import { NewsArticlesService } from '../../news-articles/news-articles.service';

export const sortByDateTime = (a: CommentData, b: CommentData): number => (a.createdDateTime < b.createdDateTime ? 1 : -1);

type CommentsState = {
  comments: CommentData[];
  isCommentsLoading: boolean;
  selectedComment: NewsArticleComment | null;
  isSelectedCommentLoading: boolean;
};

const initialState: CommentsState = {
  comments: [],
  isCommentsLoading: false,
  selectedComment: null,
  isSelectedCommentLoading: false,
};

const commentsDictionary: StateDictionary<CommentData> = {
  entityList: 'comments',
  listLoading: 'isCommentsLoading',
  selectedEntity: 'selectedComment',
  selectedEntityLoading: 'isSelectedCommentLoading',
  entityId: 'id',
  sortFn: sortByDateTime,
  createLoading: '',
};

export const CommentsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('comments-store'),
  withComputed(store => ({
    selectedCommentId: computed(() => store.selectedComment()?.id || null),
  })),
  withMethods((store, newsArticlesService = inject(NewsArticlesService), router = inject(Router), dialog = inject(MatDialog)) => {
    createStateResetHandler<CommentsState>('/marketing/news-articles-comments')(store, initialState);
    createCustomStateSetOnUrlHandler<CommentsState>('/marketing/news-articles-comments', { selectedComment: null })(store);

    const commentsMethods = {
      getAllComments: getAllWithParams<CommentData, CommentsQueryParams>({
        store,
        service: {
          getAll: (params: CommentsQueryParams) => newsArticlesService.getAllComments(params),
        },
        dictionary: commentsDictionary,
      }),

      getOneComment: getOneWithParams<NewsArticleComment, { commentId: string; articleId: string }>({
        store,
        service: {
          getOne: ({ commentId, articleId }) => newsArticlesService.getOneComment(commentId, articleId),
        },
        dictionary: commentsDictionary as unknown as StateDictionary<NewsArticleComment>,
      }),

      deleteComment: rxMethod<{ articleId: string; commentId: string }>(
        pipe(
          tap(() => patchState(store, { isCommentsLoading: true })),
          switchMap(({ articleId, commentId }) =>
            newsArticlesService.deleteComment(articleId, commentId).pipe(
              tap(() => {
                const updatedComments = store.comments().filter(comment => comment.id !== commentId);
                patchState(store, {
                  comments: updatedComments,
                  selectedComment: null,
                  isCommentsLoading: false,
                });
                router.navigate(['marketing', 'news-articles-comments']);

                const commentsFilter = JSON.parse(localStorage.getItem('commentsFilter')!);
                commentsMethods.getAllComments(commentsFilter);
              }),
              catchError(() => {
                patchState(store, { isCommentsLoading: false });
                return EMPTY;
              }),
            ),
          ),
        ),
      ),

      initializeComments: (): void => {
        const filterSettings = localStorage.getItem('commentsFilter');

        if (filterSettings !== null) {
          commentsMethods.getAllComments(JSON.parse(filterSettings));
        } else {
          const todaysDate = new Date();
          const threeDaysAgo = new Date(todaysDate.getTime() - 3 * 24 * 60 * 60 * 1000);
          const initialFilterSettings = {
            createdFromDateTime: formatToISODate(threeDaysAgo),
            createdToDateTime: formatToISODateEndOfDay(todaysDate),
          };
          localStorage.setItem('commentsFilter', JSON.stringify(initialFilterSettings));
          commentsMethods.getAllComments(initialFilterSettings);
        }
      },
    };

    const filterMethods = {
      openFilterModal: (): void => {
        const filterData = JSON.parse(localStorage.getItem('commentsFilter')!);

        dialog
          .open(CommentsFiltersComponent, {
            data: filterData,
            panelClass: 'custom-modal-container',
          })
          .afterClosed()
          // "undefined" = closed via backdrop, "null" = closed via "Cancel" button
          .pipe(take(1), filter(isNotNullOrUndefined))
          .subscribe(({ commentsQuery }) => {
            router.navigate(['marketing', 'news-articles-comments']);
            commentsMethods.getAllComments(commentsQuery);
          });
      },

      submitFilterModal: (commentsQuery: CommentsQueryParams): void => {
        localStorage.setItem('commentsFilter', JSON.stringify(commentsQuery));
      },
    };

    const navigationMethods = {
      goToCommentDetails: (articleId: string, commentId: string): void => {
        commentsMethods.getOneComment({ commentId, articleId });
        router.navigate(['marketing', 'news-articles-comments', articleId, 'comments', commentId]);
      },

      goToCommentsList: (): void => {
        patchState(store, { selectedComment: null });
        router.navigate(['marketing', 'news-articles-comments']);
      },
    };

    return {
      ...commentsMethods,
      ...filterMethods,
      ...navigationMethods,
    };
  }),
);

export interface CommentsStoreType {
  readonly comments: Signal<CommentData[]>;
  readonly isCommentsLoading: Signal<boolean>;
  readonly selectedComment: Signal<NewsArticleComment | null>;
  readonly isSelectedCommentLoading: Signal<boolean>;
  readonly selectedCommentId: Signal<string | null>;

  readonly getAllComments: (commentsQuery: CommentsQueryParams) => void;
  readonly getOneComment: (params: { commentId: string; articleId: string }) => void;
  readonly deleteComment: (params: { articleId: string; commentId: string }) => void;
  readonly initializeComments: () => void;

  readonly openFilterModal: () => void;
  readonly submitFilterModal: (commentsQuery: CommentsQueryParams) => void;

  readonly goToCommentDetails: (articleId: string, commentId: string) => void;
  readonly goToCommentsList: () => void;
}
